import { SpinningLoader } from "@risalabs_frontend_org/oasis-ui-kit";
import { Timestamp } from "firebase/firestore";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { recordWritebackQcToFirebase } from "../../../api/firebase/firestoreService";
import { COMMENTS_WRITEBACK_VALIDATION_MESSAGE } from "../../../constants/stringLiterals";
import { useBusniessOfficeNotesContext } from "../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { AllCommentsProps } from "../../../data-model/busniessOfficeNote";
import {
  shouldShowWritebackQcNotesForAuthStatus,
} from "../../../data-model/healthFacility";
import { ValidateTemplateRequestItem } from "../../../data-model/templateValidatorModel";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import {
  getCommentDateByReportId,
  getVisitDateByReportId,
} from "../../../utils/postCommentHelper";
import { buildWritebackQcMapFromValidation } from "../../../utils/writebackQcLookup";
import "./commentsWritebackValidationModal.scss";
import { useCommentsWritebackValidation } from "./useCommentsWritebackValidation";
import {
  logMakeChangesEvent,
  logProceedAnywaysEvent,
} from "./utils/logEventsForValidationModal";
import ValidationIssueItem from "./ValidationIssueItem";

interface CommentsWritebackValidationModalProps {
  validationPayload: ValidateTemplateRequestItem[];
  businessOfficeComments?: AllCommentsProps[];
  onProceedToTriggerRpa: () => void;
  onClose: () => void;
}

const CommentsWritebackValidationModal = (
  props: CommentsWritebackValidationModalProps,
) => {
  const {
    validationPayload,
    businessOfficeComments,
    onProceedToTriggerRpa,
    onClose,
  } = props;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { authOnFileDetails } = useDataAccumulatorForSingleOrder();

  const showWritebackQc = shouldShowWritebackQcNotesForAuthStatus(
    singleHealthFacilityDetails?.feature_config,
    authOnFileDetails?.auth_on_file?.auth_status,
  );

  const { setWritebackQcByReportId, rawData } = useBusniessOfficeNotesContext();

  const {
    loading,
    results,
    abort,
    validationPayloadKey,
    lastSuccessfulValidationPayloadKey,
    validationResponse,
  } = useCommentsWritebackValidation({
    validationPayload,
    onProceed: onProceedToTriggerRpa,
  });

  useEffect(() => {
    if (!showWritebackQc) return;
    if (loading) return;
    if (lastSuccessfulValidationPayloadKey !== validationPayloadKey) return;
    if (!validationResponse?.results) return;
    const next = buildWritebackQcMapFromValidation(validationResponse.results);
    setWritebackQcByReportId(next);
  }, [
    loading,
    lastSuccessfulValidationPayloadKey,
    validationPayloadKey,
    validationResponse,
    setWritebackQcByReportId,
    showWritebackQc,
  ]);

  const handleClose = () => {
    abort();
    onClose();
  };

  const handleProceedAnyway = () => {
    logProceedAnywaysEvent();
    const orderId = validationPayload[0]?.order_id;
    const orgId = validationPayload[0]?.org_id;
    const issues = results.flatMap((r) =>
      (r.parsed_content?.issues_present ?? []).map((issue) => ({
        line: issue.line,
        missing_field: issue.missing_field,
        reason: issue.reason,
      })),
    );
    if (orderId && orgId && user?.id && issues.length > 0 && showWritebackQc) {
      recordWritebackQcToFirebase({
        order_id: orderId,
        org_id: orgId,
        user_id: user.id,
        user_email: user.email ?? "",
        timestamp: Timestamp.now(),
        issues,
      });
    }
    onProceedToTriggerRpa();
  };

  const handleMakeChanges = () => {
    logMakeChangesEvent();
    handleClose();
  };

  const labelForReportIdentifier = (reportId: string) => {
    const visitDate = getVisitDateByReportId(rawData ?? [], reportId);
    if (visitDate?.trim()) return visitDate.trim();
    if (businessOfficeComments) {
      return (
        getCommentDateByReportId(businessOfficeComments, reportId) ?? reportId
      );
    }
    return reportId;
  };

  return (
    <Modal
      dialogId={ModalId.COMMENTS_WRITEBACK_VALIDATION_MODAL}
      title="Comments writeback validation"
      saveButtonText={loading ? "Verifying..." : "Make Changes"}
      cancelText="Proceed Anyway"
      onSave={handleMakeChanges}
      onCancel={handleProceedAnyway}
      onClose={handleClose}
      disableSave={loading}
    >
      <div className="comments-writeback-validation-modal flex flex-col gap-4">
        {loading && (
          <div className="flex items-center justify-center gap-1 py-6 text-small text-primaryGray-2">
            <SpinningLoader />
            Verifying your writeback comments...
          </div>
        )}

        {showWritebackQc &&
          results.some((r) => r.parsed_content?.issues_present?.length) && (
          <div className="flex flex-col items-start gap-4 p-1">
            <div className="word-wrap flex-1 text-sm font-normal">
              {COMMENTS_WRITEBACK_VALIDATION_MESSAGE}
            </div>
            {results.map((result) => {
              const issues = result.parsed_content?.issues_present;
              if (!issues?.length) return null;
              return (
                <div
                  key={result.report_identifier}
                  className="flex flex-col gap-2.5 self-stretch rounded-[0.25rem] bg-[#F7F9FA] p-2"
                >
                  <div className="text-sm font-medium">
                    {labelForReportIdentifier(result.report_identifier)}
                  </div>
                  <ul className="list-none space-y-2 pl-0">
                    {issues.map((issue, idx) => (
                      <ValidationIssueItem
                        key={`${result.report_identifier}-${idx}`}
                        issue={issue}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CommentsWritebackValidationModal;
